import DOMPurify from "dompurify";
import { Box, Stack, Typography } from "@mui/material";
import { parseDateInput } from "@utils/format-utils";

type NoteCardProps = {
  noteText: string;
  bodyHtml?: string;
  date?: string;
  visibility?: string;
  senderLabel?: string;
  showVisibilityTag?: boolean;
};

const decodeHTML = (htmlStr: string) => {
  try {
    return JSON.parse(`"${htmlStr}"`);
  } catch {
    return htmlStr;
  }
};

const formatMetaDate = (date: string) => {
  const d = parseDateInput(date);
  if (!d) return "—";

  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const NoteCard = ({
  noteText,
  bodyHtml,
  date,
  visibility,
  senderLabel,
  showVisibilityTag = true,
}: NoteCardProps) => {
  const displayBody = bodyHtml
    ? DOMPurify.sanitize(decodeHTML(bodyHtml)).split(
      "Please reply above this line",
    )[0]
    : noteText;

  const hasVisibility = Boolean(showVisibilityTag && visibility);
  const hasMeta = Boolean(date || senderLabel || hasVisibility);

  return (
    <Box
      sx={(theme) => ({
        border: "0.5px solid",
        borderColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.16)"
            : "rgba(25, 52, 68, 0.16)",
        borderRadius: "10px",
        p: 2,
        mb: 2,
      })}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {bodyHtml ? (
            <Box dangerouslySetInnerHTML={{ __html: displayBody }} />
          ) : (
            <Typography variant="body2">{displayBody}</Typography>
          )}
        </Box>

        {hasMeta && (
          <Stack
            alignItems="flex-start"
            sx={(theme) => ({
              minWidth: { xs: 88, sm: 150 },
              flexShrink: 0,
              gap: 0.75,
              borderLeft: "1px solid",
              borderLeftColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.16)"
                  : "rgba(25, 52, 68, 0.16)",
              pl: 1.5,
              ml: 0.5,
            })}
          >
            {hasVisibility && (
              <Box
                sx={{
                  alignSelf: "flex-end",
                  bgcolor: visibility === "public" ? "success.main" : "info.main",
                  px: 1.25,
                  pt: 0,
                  pb: "4px",
                  pl: 1.4,
                  borderRadius: 3,
                  mr: "-24px",
                  mt: "-24px",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#fff",
                    fontSize: ".68rem",
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    lineHeight: 1.15,
                    textTransform: "uppercase",
                  }}
                >
                  {visibility}
                </Typography>
              </Box>
            )}
            {date && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 500, lineHeight: 1.2 }}
              >
                {formatMetaDate(date)}
              </Typography>
            )}
            {senderLabel && (
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, lineHeight: 1.25 }}
              >
                {senderLabel}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};